import PptxGenJS from "pptxgenjs";
import { loadCovers, addCoverSlides } from "./pptxCovers";

/**
 * Gera um PPTX executivo do Balanced Scorecard (para a diretoria).
 * Identidade Marply: bordô + grafite, acento amarelo.
 *
 * @param {Object}   p
 * @param {Array}    p.scorecardData    perspectivas -> objetivos -> indicadores
 * @param {string}   p.selectedPeriod   ex.: "mai-2026"
 * @param {Object}   p.globalPerf       { performance, periodLabel, previous, trend, perspectives[] }
 */
export async function generateScorecardPPTX({ scorecardData, selectedPeriod, globalPerf, covers: coversOverride }) {
  // ---- Paleta Marply ----
  const BORDO = "7A2433";
  const GRAFITE = "2B2B30";
  const AMARELO = "E8D833";
  const CINZA_BG = "F5F6F8";
  const CINZA_LN = "E5E7EB";
  const CINZA_TX = "475569";
  const CINZA_MUT = "9CA3AF";
  const BRANCO = "FFFFFF";

  // ---- Helpers ----
  const perfHex = (v) => (v >= 100 ? "059669" : v >= 80 ? "D97706" : "DC2626");

  const formatValue = (value, unit) => {
    if (typeof value === "number" && !isNaN(value)) {
      if (unit === "R$") {
        return `R$ ${value.toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
      }
      if (unit === "%") return `${value.toFixed(2)}%`;
      return `${value.toFixed(2)}${unit ? " " + unit : ""}`;
    }
    return "N/D";
  };

  const trendInfo = (t) => {
    switch (t) {
      case "upV": return { c: "↗", color: "059669" };
      case "upR": return { c: "↗", color: "DC2626" };
      case "downR": return { c: "↘", color: "DC2626" };
      case "downV": return { c: "↘", color: "059669" };
      case "up": return { c: "↗", color: "059669" };
      case "down": return { c: "↘", color: "DC2626" };
      case "w":
      case "stable": return { c: "—", color: "2563EB" };
      default: return { c: "–", color: CINZA_MUT };
    }
  };

  const statusInfo = (s) =>
    s === "T" ? { c: "✓", color: "059669" }
      : s === "N" ? { c: "!", color: "D97706" }
        : { c: "N/D", color: CINZA_MUT };

  const data = Array.isArray(scorecardData) ? scorecardData : [];
  const periodLabel = (globalPerf && globalPerf.periodLabel) || selectedPeriod || "";
  const periodUpper = (selectedPeriod || "").toUpperCase();

  // Lista de perspectivas p/ o sumário (usa globalPerf; senão deriva do scorecard)
  const perspList =
    globalPerf && Array.isArray(globalPerf.perspectives) && globalPerf.perspectives.length
      ? globalPerf.perspectives.map((p) => ({ name: p.perspective, value: p.performance || 0 }))
      : data.map((p) => ({ name: p.perspective, value: p.average || 0 }));

  const globalValue =
    globalPerf && typeof globalPerf.performance === "number"
      ? globalPerf.performance
      : perspList.length
        ? perspList.reduce((s, p) => s + (p.value || 0), 0) / perspList.length
        : 0;

  const today = new Date().toLocaleDateString("pt-BR");

  // ---- Documento ----
  const pptx = new PptxGenJS();
  pptx.defineLayout({ name: "MARPLY", width: 13.333, height: 7.5 });
  pptx.layout = "MARPLY";
  pptx.author = "Marply BSC";
  pptx.company = "Marply Indústria de Compensados";
  pptx.subject = "Balanced Scorecard";
  pptx.title = `Balanced Scorecard — ${periodUpper}`;

  const PW = 13.333;
  const FOOTER = "Marply · Balanced Scorecard · Confidencial — uso interno da Diretoria";

  const addFooter = (slide, pageNo) => {
    slide.addText(FOOTER, {
      x: 0.5, y: 7.1, w: 10.5, h: 0.3, fontFace: "Arial",
      fontSize: 8, color: CINZA_MUT, align: "left", valign: "middle",
    });
    if (pageNo) {
      slide.addText(String(pageNo), {
        x: 12.4, y: 7.1, w: 0.5, h: 0.3, fontFace: "Arial",
        fontSize: 8, color: CINZA_MUT, align: "right", valign: "middle",
      });
    }
  };

  // ================= CAPA + ABERTURA (template Marini) =================
  const covers = await loadCovers("scorecard", coversOverride);
  const introCount = addCoverSlides(pptx, covers, PW, 7.5);

  // Capa textual padrão (fallback)
  if (introCount === 0) {
    const s = pptx.addSlide();
    s.background = { color: BORDO };
    // faixa grafite lateral
    s.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: 0.35, h: 7.5, fill: { color: AMARELO } });
    s.addShape(pptx.ShapeType.rect, { x: 0.35, y: 0, w: 0.12, h: 7.5, fill: { color: GRAFITE } });

    s.addText("BALANCED SCORECARD", {
      x: 1.0, y: 2.5, w: 11.4, h: 1.1, fontFace: "Arial",
      fontSize: 46, bold: true, color: BRANCO, align: "left",
    });
    s.addShape(pptx.ShapeType.rect, { x: 1.05, y: 3.65, w: 3.2, h: 0.06, fill: { color: AMARELO } });
    s.addText("Marini · Indústria de Compensados", {
      x: 1.0, y: 3.85, w: 11.4, h: 0.5, fontFace: "Arial",
      fontSize: 22, color: AMARELO, align: "left",
    });
    s.addText(
      [
        { text: "Período de Referência: ", options: { bold: false, color: "E7CDD3" } },
        { text: periodUpper, options: { bold: true, color: BRANCO } },
      ],
      { x: 1.0, y: 4.55, w: 11.4, h: 0.4, fontFace: "Arial", fontSize: 16, align: "left" }
    );
    s.addText(`Gerado em: ${today}`, {
      x: 1.0, y: 4.95, w: 11.4, h: 0.4, fontFace: "Arial",
      fontSize: 13, color: "E7CDD3", align: "left",
    });
    s.addText("Relatório executivo — Diretoria", {
      x: 1.0, y: 6.7, w: 11.4, h: 0.4, fontFace: "Arial",
      fontSize: 11, italic: true, color: "E7CDD3", align: "left",
    });
  }

  // ================= SLIDE 2 — SUMÁRIO EXECUTIVO =================
  {
    const s = pptx.addSlide();
    s.background = { color: BRANCO };
    // cabeçalho
    s.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: PW, h: 0.9, fill: { color: BORDO } });
    s.addShape(pptx.ShapeType.rect, { x: 0, y: 0.9, w: PW, h: 0.06, fill: { color: AMARELO } });
    s.addText("Sumário Executivo", {
      x: 0.5, y: 0, w: 8, h: 0.9, fontFace: "Arial",
      fontSize: 24, bold: true, color: BRANCO, valign: "middle",
    });
    s.addText(periodUpper, {
      x: PW - 4.5, y: 0, w: 4.0, h: 0.9, fontFace: "Arial",
      fontSize: 16, color: "F1D9DE", align: "right", valign: "middle",
    });

    // bloco performance global (centralizado no topo)
    s.addText("PERFORMANCE GLOBAL", {
      x: 0.5, y: 1.35, w: PW - 1, h: 0.4, fontFace: "Arial",
      fontSize: 16, bold: true, color: CINZA_TX, align: "center", charSpacing: 2,
    });
    s.addText(`${globalValue.toFixed(1)}%`, {
      x: 0.5, y: 1.7, w: PW - 1, h: 1.5, fontFace: "Arial",
      fontSize: 88, bold: true, color: perfHex(globalValue), align: "center",
    });
    if (globalPerf && globalPerf.previous != null) {
      const diff = globalValue - globalPerf.previous;
      const ti = trendInfo(globalPerf.trend === "up" ? "upV" : globalPerf.trend === "down" ? "downR" : "w");
      s.addText(`${ti.c} ${diff.toFixed(1)} p.p. vs. período anterior`, {
        x: 0.5, y: 3.2, w: PW - 1, h: 0.4, fontFace: "Arial",
        fontSize: 15, bold: true, color: ti.color, align: "center",
      });
    }

    // KPI cards por perspectiva — 1 linha de 4 cards grandes
    const nCards = Math.min(perspList.length, 4);
    if (nCards > 0) {
      const gap = 0.3;
      const cardH = 2.15;
      const cardY = 4.0;
      const totalW = 12.3;
      const cardW = (totalW - gap * (nCards - 1)) / nCards;
      const startX = (PW - totalW) / 2;
      perspList.slice(0, 4).forEach((p, i) => {
        const x = startX + i * (cardW + gap);
        const col = perfHex(p.value);
        s.addShape(pptx.ShapeType.roundRect, {
          x, y: cardY, w: cardW, h: cardH, rectRadius: 0.1,
          fill: { color: CINZA_BG }, line: { color: col, width: 1.75 },
        });
        // faixa colorida no topo do card
        s.addShape(pptx.ShapeType.rect, { x: x + 0.12, y: cardY + 0.22, w: cardW - 0.24, h: 0.06, fill: { color: col } });
        s.addText(p.name, {
          x: x + 0.15, y: cardY + 0.42, w: cardW - 0.3, h: 0.5, fontFace: "Arial",
          fontSize: 16, color: GRAFITE, bold: true, align: "center", valign: "middle",
        });
        s.addText(`${(p.value || 0).toFixed(1)}%`, {
          x: x + 0.15, y: cardY + 0.95, w: cardW - 0.3, h: 1.0, fontFace: "Arial",
          fontSize: 40, bold: true, color: col, align: "center", valign: "middle",
        });
      });
    }
    addFooter(s, introCount + 1);
  }

  // ================= SLIDES POR PERSPECTIVA =================
  // Estrutura idêntica à tela: Objetivo | Status(média do obj.) | Item |
  // Histórico(3) | período(Realizado/Meta/%) | Status T | Trend.
  // Paginação MANUAL: o cabeçalho (com a perspectiva) é repetido em TODO slide
  // e o rowspan do objetivo nunca é quebrado entre páginas.
  const histLabels = getHistoricalPeriods(selectedPeriod);
  const COLW = [2.1, 0.9, 2.1, 0.98, 0.98, 0.98, 1.1, 1.1, 0.78, 0.72, 0.72];
  const TABLE_W = COLW.reduce((a, b) => a + b, 0);
  const TABLE_X = (PW - TABLE_W) / 2;

  const BLUE = "2557D6", GRAY_GRP = "6B7280", GREEN_GRP = "0F9D63";
  const GRAY_SUB = "808A99", GREEN_SUB = "34C88A";
  const GREEN_BAND = "D6F5E6", GREEN_BAND_TX = "065F46";

  const hCell = (t, extra) => ({
    text: t,
    options: Object.assign(
      { fill: { color: BLUE }, color: "FFFFFF", bold: true, fontSize: 9, align: "center", valign: "middle", fontFace: "Arial", margin: [2, 3, 2, 3] },
      extra || {}
    ),
  });

  const objCellOpts = { fill: { color: "F8FAFC" }, color: "1E293B", bold: true, fontSize: 9.5, align: "left", valign: "middle", fontFace: "Arial", margin: [3, 6, 3, 6] };
  const itemCellOpts = { color: "1F2937", fontSize: 9.5, align: "left", valign: "middle", fontFace: "Arial", margin: [3, 6, 3, 6] };
  const histCellOpts = { color: "6B7280", fontSize: 9, align: "center", valign: "middle", fontFace: "Arial", margin: [2, 4, 2, 4] };
  const curCellOpts = { fill: { color: "EAFBF3" }, color: "065F46", bold: true, fontSize: 9, align: "center", valign: "middle", fontFace: "Arial", margin: [2, 4, 2, 4] };
  const ctrCellOpts = { color: "1F2937", fontSize: 9, align: "center", valign: "middle", fontFace: "Arial", margin: [2, 4, 2, 4] };

  const buildHeader = (perspName) => {
    return [
      [{ text: (perspName || "").toUpperCase(), options: { colspan: 11, fill: { color: GREEN_BAND }, color: GREEN_BAND_TX, bold: true, fontSize: 13, align: "left", valign: "middle", fontFace: "Arial", margin: [3, 10, 3, 10] } }],
      [
        hCell("OBJETIVO ESTRATÉGICO", { rowspan: 2, align: "left" }),
        hCell("STATUS", { rowspan: 2 }),
        hCell("ITEM DE CONTROLE", { rowspan: 2, align: "left" }),
        hCell("HISTÓRICO", { colspan: 3, fill: { color: GRAY_GRP } }),
        hCell(periodUpper, { colspan: 3, fill: { color: GREEN_GRP } }),
        hCell("STATUS T", { rowspan: 2 }),
        hCell("TREND", { rowspan: 2 }),
      ],
      [
        hCell((histLabels[0] || "H-3").toUpperCase(), { fill: { color: GRAY_SUB } }),
        hCell((histLabels[1] || "H-2").toUpperCase(), { fill: { color: GRAY_SUB } }),
        hCell((histLabels[2] || "H-1").toUpperCase(), { fill: { color: GRAY_SUB } }),
        hCell("REALIZADO", { fill: { color: GREEN_SUB } }),
        hCell("META", { fill: { color: GREEN_SUB } }),
        hCell("%", { fill: { color: GREEN_SUB } }),
      ],
    ];
  };

  const histVal = (ind, lbl) => {
    const h = (ind.history || []).find((x) => x.periodLabel === lbl);
    return h && h.percent != null ? formatValue(h.percent, ind.unit) : "N/D";
  };

  const dataRow = (ind, obj, first, span) => {
    const cur = ind.current || {};
    const st = statusInfo(cur.statusT);
    const tr = trendInfo(cur.trend);
    const pct = typeof cur.percent === "number" ? cur.percent : null;
    const cells = [];
    if (first) {
      const a = obj.average || 0;
      const band = a >= 100 ? ["D1FAE5", "059669"] : a >= 80 ? ["FEF3C7", "D97706"] : ["FEE2E2", "DC2626"];
      cells.push({ text: obj.objective || "", options: { ...objCellOpts, rowspan: span } });
      cells.push({ text: `${a.toFixed(2)}%`, options: { rowspan: span, fill: { color: band[0] }, color: band[1], bold: true, fontSize: 10, align: "center", valign: "middle", fontFace: "Arial" } });
    }
    cells.push({ text: ind.indicator || "", options: itemCellOpts });
    cells.push({ text: histVal(ind, histLabels[0]), options: histCellOpts });
    cells.push({ text: histVal(ind, histLabels[1]), options: histCellOpts });
    cells.push({ text: histVal(ind, histLabels[2]), options: histCellOpts });
    cells.push({ text: cur.realized != null ? formatValue(cur.realized, ind.unit) : "N/D", options: curCellOpts });
    cells.push({ text: cur.target != null ? formatValue(cur.target, ind.unit) : "N/D", options: curCellOpts });
    cells.push({ text: pct != null ? `${pct.toFixed(1)}%` : "N/D", options: { ...curCellOpts, fill: { color: "E6F7EF" }, color: pct != null ? perfHex(pct) : CINZA_MUT } });
    cells.push({ text: st.c, options: { ...ctrCellOpts, color: st.color, bold: true, fontSize: 11 } });
    cells.push({ text: tr.c, options: { ...ctrCellOpts, color: tr.color, bold: true, fontSize: 12 } });
    return cells;
  };

  const tableOpts = () => ({
    x: TABLE_X, y: 0.4, w: TABLE_W, colW: COLW,
    border: { type: "solid", color: CINZA_LN, pt: 0.5 },
    valign: "middle", fontFace: "Arial", fontSize: 9, rowH: 0.3, margin: [2, 4, 2, 4],
  });

  let pageNo = introCount + 2;
  const MAX_ROWS = 11; // linhas de indicador por slide (deixa folga p/ o rodapé)
  data.forEach((persp) => {
    const entries = [];
    (persp.objectives || []).forEach((obj) =>
      (obj.indicators || []).forEach((ind) => entries.push({ ind, obj }))
    );

    if (entries.length === 0) {
      const s = pptx.addSlide();
      s.background = { color: BRANCO };
      s.addTable(buildHeader(persp.perspective), tableOpts());
      addFooter(s, pageNo++);
      return;
    }

    for (let i = 0; i < entries.length; i += MAX_ROWS) {
      const chunk = entries.slice(i, i + MAX_ROWS);
      const rows = buildHeader(persp.perspective);
      let j = 0;
      while (j < chunk.length) {
        const obj = chunk[j].obj;
        let k = j;
        while (k < chunk.length && chunk[k].obj === obj) k++;
        const span = k - j;
        for (let m = j; m < k; m++) rows.push(dataRow(chunk[m].ind, obj, m === j, span));
        j = k;
      }
      const s = pptx.addSlide();
      s.background = { color: BRANCO };
      s.addTable(rows, tableOpts());
      addFooter(s, pageNo++);
    }
  });

  // ================= SLIDE FINAL — LEGENDA & METODOLOGIA =================
  {
    const s = pptx.addSlide();
    s.background = { color: BRANCO };
    s.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: PW, h: 0.9, fill: { color: GRAFITE } });
    s.addShape(pptx.ShapeType.rect, { x: 0, y: 0.9, w: PW, h: 0.06, fill: { color: AMARELO } });
    s.addText("Legenda & Metodologia", {
      x: 0.5, y: 0, w: 10, h: 0.9, fontFace: "Arial",
      fontSize: 22, bold: true, color: BRANCO, valign: "middle",
    });

    const bullets = [
      { text: "STATUS  ✓ = meta atingida   |   ! = meta não atingida   |   N/D = sem dado", options: { bullet: true, color: GRAFITE, fontSize: 15, paraSpaceAfter: 10 } },
      { text: "TREND  ↗ alta   |   ↘ baixa   |   — estável  (verde = favorável, vermelho = desfavorável)", options: { bullet: true, color: GRAFITE, fontSize: 15, paraSpaceAfter: 10 } },
      { text: "FAIXAS DE %  Verde ≥ 100%   |   Amarelo ≥ 80%   |   Vermelho < 80%", options: { bullet: true, color: GRAFITE, fontSize: 15, paraSpaceAfter: 10 } },
      { text: "PERFORMANCE  cálculo ponderado pelos pesos de indicador, objetivo e perspectiva (média ponderada normalizada).", options: { bullet: true, color: GRAFITE, fontSize: 15, paraSpaceAfter: 10 } },
      { text: "HISTÓRICO  os três períodos anteriores ao de referência (quando disponíveis).", options: { bullet: true, color: GRAFITE, fontSize: 15 } },
    ];
    s.addText(bullets, { x: 0.7, y: 1.4, w: 12, h: 4.5, fontFace: "Arial", align: "left", valign: "top" });

    // faixa de cores
    const legY = 6.0;
    [["≥ 100%", "059669"], ["≥ 80%", "D97706"], ["< 80%", "DC2626"]].forEach(([lbl, col], i) => {
      const x = 0.7 + i * 2.6;
      s.addShape(pptx.ShapeType.roundRect, { x, y: legY, w: 0.45, h: 0.45, rectRadius: 0.06, fill: { color: col } });
      s.addText(lbl, { x: x + 0.55, y: legY, w: 1.9, h: 0.45, fontFace: "Arial", fontSize: 14, bold: true, color: GRAFITE, valign: "middle" });
    });
    addFooter(s, pageNo);
  }

  // 3 períodos históricos anteriores ao selecionado (mesma lógica do PDF)
  function getHistoricalPeriods(sp) {
    if (!sp) return ["H-3", "H-2", "H-1"];
    const monthMap = { jan: 1, fev: 2, mar: 3, abr: 4, mai: 5, jun: 6, jul: 7, ago: 8, set: 9, out: 10, nov: 11, dez: 12 };
    const rev = { 1: "jan", 2: "fev", 3: "mar", 4: "abr", 5: "mai", 6: "jun", 7: "jul", 8: "ago", 9: "set", 10: "out", 11: "nov", 12: "dez" };
    let month, year;
    if (sp.includes("-") && sp.match(/^[a-z]{3}-\d{4}$/i)) {
      const [m, y] = sp.split("-");
      month = monthMap[m.toLowerCase()];
      year = parseInt(y);
    } else if (sp.includes("/")) {
      const [m, y] = sp.split("/");
      month = monthMap[m.toLowerCase()];
      year = parseInt(`20${y}`);
    }
    if (!month || !year) return ["H-3", "H-2", "H-1"];
    const out = [];
    for (let i = 3; i >= 1; i--) {
      let cm = month - i, cy = year;
      if (cm <= 0) { cm += 12; cy -= 1; }
      const ms = rev[cm];
      const ys = sp.includes("/") ? cy.toString().slice(-2) : cy.toString();
      const sep = sp.includes("/") ? "/" : "-";
      out.push(`${ms}${sep}${ys}`);
    }
    return out;
  }

  const stamp = new Date().toISOString().split("T")[0];
  const fileName = `balanced-scorecard-${selectedPeriod || "periodo"}-${stamp}.pptx`;
  await pptx.writeFile({ fileName });
  return fileName;
}
